/**
 * Joining from an invitation: the link in the email opens a Create account page
 * scoped to that one invitation (docs/PERMISSIONS.md → "Joining from an
 * invitation").
 *
 * Pure rules, so the real server, Test mode and mock mode all decide exactly
 * the same way — and so every branch can be tested without a database.
 */
import { z } from 'zod'
import { type AssignableRole, assignableRoles } from '../access/index.ts'
import { namePart } from './name.ts'

/** How long an invitation link works. Better Auth's own setting reads this too. */
export const INVITATION_LIFETIME_HOURS = 48

/**
 * How long a "confirm your email" link works — Better Auth's setting, and the
 * wording in the email, both read this.
 */
export const EMAIL_CONFIRMATION_LIFETIME_HOURS = 1

/**
 * The shortest password anyone may set — the same rule for signing up, being
 * invited and resetting. Better Auth enforces it on the server as well.
 */
export const PASSWORD_MIN_LENGTH = 12
/** Better Auth's own ceiling; longer is refused rather than silently cut. */
export const PASSWORD_MAX_LENGTH = 128

/**
 * How soon after resending an invitation it can be resent again — a race
 * backstop, not a real limit (D-006). Measured from when the current link was
 * made (`invitation_link.created_at`), no new column.
 */
export const INVITATION_RESEND_COOLDOWN_SECONDS = 60

/**
 * What a link can be when someone opens it. `ready` is the only one that shows
 * the form; every other state says exactly what happened, in plain words.
 */
export const invitationStates = ['ready', 'expired', 'revoked', 'used'] as const
export type InvitationState = (typeof invitationStates)[number]

/** The facts about an invitation that decide whether its link still works. */
export interface InvitationFacts {
  /** Better Auth's status: pending, accepted, canceled or rejected. */
  status: string
  expiresAt: Date
  /** When the link was used to create an account. Null until then. */
  usedAt: Date | null
}

export function invitationStateOf(facts: InvitationFacts, now = new Date()): InvitationState {
  // Used first: a link that did its job reads as used even after it expires.
  if (facts.usedAt || facts.status === 'accepted') return 'used'
  if (facts.status === 'canceled' || facts.status === 'rejected') return 'revoked'
  if (facts.status !== 'pending') return 'revoked'
  if (facts.expiresAt.getTime() <= now.getTime()) return 'expired'
  return 'ready'
}

/**
 * What the Create account form sends. Validated on the server, whatever the
 * browser checked. Anything else in the body — a `role`, another invitation's
 * id — is dropped here, never read: the role always comes from the invitation.
 */
export const createAccountInputSchema = z.object({
  token: z.string().min(20).max(200),
  email: z.email('Enter a valid email address.').transform((email) => email.trim().toLowerCase()),
  firstName: namePart('Enter your first name.'),
  lastName: namePart('Enter your last name.'),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(PASSWORD_MAX_LENGTH, `Use ${PASSWORD_MAX_LENGTH} characters or fewer.`),
})
export type CreateAccountInput = z.infer<typeof createAccountInputSchema>

/** What the page is told about a link. Only ever about that one invitation. */
export const invitationPreviewSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('ready'),
    email: z.email(),
    role: z.enum(assignableRoles),
    inviterName: z.string(),
    organizationName: z.string(),
    expiresAt: z.iso.datetime(),
    /** The invited address already has an account — sign in instead. */
    accountExists: z.boolean(),
  }),
  z.object({
    state: z.enum(['expired', 'revoked', 'used']),
    inviterName: z.string(),
    organizationName: z.string(),
  }),
  /** No such link: mistyped, forged, or from an invitation that was deleted. */
  z.object({ state: z.literal('invalid') }),
])
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>

export const createAccountResultSchema = z.discriminatedUnion('outcome', [
  /** Same address as invited: the click proved the inbox. Joined and signed in. */
  z.object({ outcome: z.literal('signed-in') }),
  /** A different address: the account exists, but nothing opens until it's confirmed. */
  z.object({ outcome: z.literal('verify-email'), email: z.email() }),
  /** The invited address already has an account: sign in to accept. */
  z.object({ outcome: z.literal('account-exists'), email: z.email() }),
  /** The address they changed to belongs to someone else. Nothing was created. */
  z.object({ outcome: z.literal('email-taken'), email: z.email() }),
  /** The link can't be used — and why. Nothing was created. */
  z.object({
    outcome: z.literal('unavailable'),
    state: z.enum(['expired', 'revoked', 'used', 'invalid']),
  }),
])
export type CreateAccountResult = z.infer<typeof createAccountResultSchema>

/** What to do with a Create account submission — decided before anything is written. */
export type CreateAccountDecision =
  | { kind: 'refuse'; result: CreateAccountResult }
  /** Same address: verified by the click, join now with the invited role. */
  | { kind: 'join'; email: string; role: AssignableRole }
  /** Changed address: create the account unverified and send a confirmation link. */
  | { kind: 'verify'; email: string; role: AssignableRole }

export function decideCreateAccount(args: {
  input: CreateAccountInput
  /** The invitation behind the token, or null when the token matches nothing. */
  invitation: (InvitationFacts & { email: string; role: AssignableRole }) | null
  /** Whether an account already uses this address. */
  accountExistsFor: (email: string) => boolean
  now?: Date
}): CreateAccountDecision {
  const { input, invitation } = args
  if (!invitation) return { kind: 'refuse', result: { outcome: 'unavailable', state: 'invalid' } }
  const state = invitationStateOf(invitation, args.now)
  if (state !== 'ready') return { kind: 'refuse', result: { outcome: 'unavailable', state } }

  const invitedEmail = invitation.email.trim().toLowerCase()
  const sameEmail = input.email === invitedEmail
  if (args.accountExistsFor(input.email)) {
    return {
      kind: 'refuse',
      result: sameEmail
        ? { outcome: 'account-exists', email: input.email }
        : { outcome: 'email-taken', email: input.email },
    }
  }
  return sameEmail
    ? { kind: 'join', email: input.email, role: invitation.role }
    : { kind: 'verify', email: input.email, role: invitation.role }
}

/** Everything known about one invitation behind a link. */
export interface InvitationDetails extends InvitationFacts {
  email: string
  role: AssignableRole
  inviterName: string
  organizationName: string
}

/** What the Create account page is told about a link. */
export function previewOf(
  invitation: InvitationDetails | null,
  accountExists: boolean,
  now = new Date(),
): InvitationPreview {
  if (!invitation) return { state: 'invalid' }
  const state = invitationStateOf(invitation, now)
  const who = { inviterName: invitation.inviterName, organizationName: invitation.organizationName }
  if (state !== 'ready') return { state, ...who }
  return {
    state,
    ...who,
    email: invitation.email.trim().toLowerCase(),
    role: invitation.role,
    expiresAt: invitation.expiresAt.toISOString(),
    accountExists,
  }
}

/**
 * What clicking the confirmation link for a changed address does. The account
 * is only made here — never before — so an address typed in by someone who
 * doesn't own it is never taken (docs/PERMISSIONS.md).
 */
export const confirmSignupResultSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('signed-in') }),
  /** Someone made an account with this address before it was confirmed. */
  z.object({ outcome: z.literal('email-taken'), email: z.email() }),
  z.object({
    outcome: z.literal('unavailable'),
    state: z.enum(['expired', 'revoked', 'used', 'invalid']),
  }),
])
export type ConfirmSignupResult = z.infer<typeof confirmSignupResultSchema>

export const confirmSignupInputSchema = z.object({ token: z.string().min(20).max(200) })
